// AuthProvider / CartProvider use the canonical "fetch on mount and on user change"
// pattern. The internal/external eslint-react v6 rule "set-state-in-effect" flags
// this as a false positive — see CODE_QUALITY_NOTES.md.
import { useEffect, useState, useRef, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider } from "@/context/LangContext";
import PrelaunchGate from "@/components/PrelaunchGate";
import CoaVerifyPage from "@/pages/CoaVerifyPage";
import IntroSplash from "@/components/IntroSplash";
import ResearchAssistant from "@/components/ResearchAssistant";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { useWebVitals } from "@/hooks/usePerformance";

// Critical path: HomePage loaded eagerly for fast LCP
import HomePage from "@/pages/HomePage";

// Route-level code splitting — every other page lazy loaded
const ShopPage = lazy(() => import("@/pages/ShopPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
// Research Library pages removed from routing for compliance (hidden for now)
const BatchVerifyPage = lazy(() => import("@/pages/BatchVerifyPage"));
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthPage").then(m => ({ default: m.AuthCallbackPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const ReferralLanding = lazy(() => import("@/pages/ReferralLanding"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const ManualPaymentOrderPage = lazy(() => import("@/pages/ManualPaymentOrderPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const CommunityPage = lazy(() => import("@/pages/CommunityPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const FDADisclaimerPage = lazy(() => import("@/pages/legal/FDADisclaimerPage"));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/legal/PrivacyPage"));
const RefundPage = lazy(() => import("@/pages/legal/RefundPage"));
const PurchaserResponsibilityPage = lazy(() => import("@/pages/legal/PurchaserResponsibilityPage"));
const ShippingPage = lazy(() => import("@/pages/legal/ShippingPage"));
const ReferralTermsPage = lazy(() => import("@/pages/legal/ReferralTermsPage"));
const PaymentReturnPage = lazy(() => import("@/pages/PaymentReturnPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const TagadaReturnPage = lazy(() => import("@/pages/TagadaReturnPage"));

// Lazy CheckoutSuccessPage
const CheckoutSuccessPage = lazy(() =>
  import("@/pages/CheckoutPage").then(m => ({ default: m.CheckoutSuccessPage }))
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send httpOnly cookies on every axios request so session persists across
// browser refreshes and cross-origin deployments (e.g. Cloudflare-proxied
// frontend + API on different subdomains). Harmless for same-origin.
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Cart Context
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};

// Auth Provider — Hybrid: httpOnly cookie + in-memory token fallback
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  const authHeaders = () => tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {};

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setUser(res.data);
    } catch {
      tokenRef.current = null;
      setUser(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, remember = false) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, remember });
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  // Exchanges Emergent Google session_id → verified user + httpOnly cookie.
  const googleSession = async (sessionId, remember = false) => {
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId, remember });
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (firstName, lastName, email, password, acceptances) => {
    const res = await axios.post(`${API}/auth/register`, {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      accepted_terms: acceptances?.terms ?? true,
      accepted_privacy: acceptances?.privacy ?? true,
      accepted_disclaimer: acceptances?.disclaimer ?? true,
      email_updates: acceptances?.email_updates ?? false,
    }, { headers: { "X-Referral-Token": localStorage.getItem("hexxium_ref_attr") || "" } });
    tokenRef.current = res.data.access_token;
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
    } catch {
      // proceed regardless
    }
    tokenRef.current = null;
    setUser(null);
  };

  const getAuthHeaders = () => authHeaders();

  return (
    <AuthContext.Provider
      value={{ user, login, register, logout, getAuthHeaders, loading, googleSession }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Cart Provider
const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const { user, getAuthHeaders } = useAuth();

  // Cart item count (sum of quantities) — consumed by Navbar badge
  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 0), 0);

  const fetchCart = async () => {
    try {
      const res = await axios.get(`${API}/cart`, { headers: getAuthHeaders() });
      const cartItems = res.data || [];
      setCart(cartItems);
      const total = cartItems.reduce((sum, item) => {
        const unitPrice = item.discounted_unit_price ?? (item.variant_price || item.product?.price || 0);
        return sum + (unitPrice * item.quantity);
      }, 0);
      setCartTotal(total);
    } catch {
      setCart([]);
      setCartTotal(0);
    }
  };

  useEffect(() => {
    if (user) fetchCart();
    else { setCart([]); setCartTotal(0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const addToCart = async (productId, quantity = 1, batchId = null, variantId = null, variantName = null, variantPrice = null) => {
    // Preserve scroll position to prevent mobile page jump when cart state update
    // triggers a component re-render that swaps layout around the button.
    const scrollY = window.scrollY;
    try {
      await axios.post(`${API}/cart`, {
        product_id: productId, quantity, batch_id: batchId,
        variant_id: variantId, variant_name: variantName, variant_price: variantPrice,
      }, { headers: getAuthHeaders() });
      await fetchCart();
      import("@/lib/ttq").then((m) => m.ttqTrack("AddToCart"));
      import("@/lib/fbq").then((m) => m.fbqAddToCart());
      // Restore scroll position after state flush (mobile Safari reflow prevention)
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
      return true;
    } catch (err) {
      // Don't silently swallow — surface failure to caller + log
      console.error("[CART] Add to cart failed:", err?.response?.status, err?.response?.data);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
      throw err;
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    try {
      await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`, {}, { headers: getAuthHeaders() });
      await fetchCart();
      return true;
    } catch (err) {
      console.error("[CART] Update qty failed:", err?.response?.status);
      throw err;
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { headers: getAuthHeaders() });
      await fetchCart();
      return true;
    } catch (err) {
      console.error("[CART] Remove failed:", err?.response?.status);
      throw err;
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart`, { headers: getAuthHeaders() });
      await fetchCart();
      return true;
    } catch (err) {
      console.error("[CART] Clear failed:", err?.response?.status);
      throw err;
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        cartTotal,
        cartCount,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

import { AdminAuthProvider, useAdminAuth } from "@/context/AdminAuthContext";
import AgeVerification from "@/components/AgeVerification";
import { CookieConsent } from "@/components/shared/CookieConsent";

// TikTok Pixel SPA page views — the base code fires ttq.page() once on initial
// load; this fires it on every client-side route change (consent-gated by the SDK).
const TrackPageViews = () => {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    try { window.ttq?.page(); } catch { /* pixel blocked */ }
    try { window.fbq?.("track", "PageView"); } catch { /* pixel blocked */ }
  }, [pathname]);
  return null;
};

// Scroll to top on every route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Protected Route for customer pages
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

// Protected Route for admin pages — uses separate admin auth
const AdminProtectedRoute = ({ children }) => {
  const { admin, authStep, loading } = useAdminAuth();
  if (loading) return <PageSkeleton />;
  if (!admin || authStep !== "complete") return <AdminLogin />;
  return children;
};

function App() {
  useWebVitals();

  return (
    <BrowserRouter>
      <LangProvider>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <AgeVerification />
          <CookieConsent />
          <TrackPageViews />
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageSkeleton />}>
            <IntroSplash />
            <PrelaunchGate>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/r/:code" element={<ReferralLanding />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/register" element={<AuthPage />} />
              <Route path="/auth-callback" element={<AuthCallbackPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/fda-disclaimer" element={<FDADisclaimerPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/referral-terms" element={<ReferralTermsPage />} />
              <Route path="/order/payment-return/:orderId" element={<PaymentReturnPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/refund-policy" element={<RefundPage />} />
              <Route path="/purchaser-responsibility" element={<PurchaserResponsibilityPage />} />
              <Route path="/shipping-policy" element={<ShippingPage />} />
              {/* Public COA lookup — target of vial-label QR codes (no login required) */}
              <Route path="/coa/:code" element={<CoaVerifyPage />} />
              {/* Gated routes — require login */}
              <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
              <Route path="/product/:productId" element={<ProtectedRoute><ProductPage /></ProtectedRoute>} />
              {/* Research Library hidden for compliance — redirect home */}
              <Route path="/research" element={<Navigate to="/" replace />} />
              <Route path="/research/:articleId" element={<Navigate to="/" replace />} />
              <Route path="/verify" element={<ProtectedRoute><BatchVerifyPage /></ProtectedRoute>} />
              <Route path="/about" element={<ProtectedRoute><AboutPage /></ProtectedRoute>} />
              <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
              <Route path="/checkout" element={
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              } />
              <Route path="/checkout/success" element={
                <ProtectedRoute>
                  <CheckoutSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/checkout/tagada/return" element={
                <ProtectedRoute>
                  <TagadaReturnPage />
                </ProtectedRoute>
              } />
              <Route path="/checkout/manual-payment/:orderId" element={
                <ProtectedRoute>
                  <ManualPaymentOrderPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/*" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/admin/*" element={
                <AdminAuthProvider>
                  <AdminProtectedRoute>
                    <AdminPage />
                  </AdminProtectedRoute>
                </AdminAuthProvider>
              } />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </PrelaunchGate>
          </Suspense>
          <ResearchAssistant />
        </CartProvider>
      </AuthProvider>
      </LangProvider>
    </BrowserRouter>
  );
}

export default App;
